import { validateEmpty } from 'utils/ValidateUtils';
import { BasicOutdoorFormModel, OutdoorFormProps } from './OutdoorFormModel';
import { OutdoorForm } from './OutdoorForm';
import i18n from 'i18n';
import { floor } from 'lodash';
import moment from 'moment';
import {
  DynamicCreativeTypeSetting,
  ScreenDisplayType
} from 'core/channelGroup/ChannelGroup';
import {
  CreativeApprovalConfig,
  CreativeSize,
  OutdoorType
} from 'core/creative/Creative';
import {
  getOutdoorTypeLabel,
  isEpaperChannelGroup
} from 'core/channelGroup/EpaperChannelGroup';

export class DynamicOutdoorFormModel extends BasicOutdoorFormModel<DynamicOutdoorFormModel> {
  constructor(
    advertiserId: number,
    public creativeApprovalConfigs: CreativeApprovalConfig[],
    private creativeTypeSetting: DynamicCreativeTypeSetting,
    forPmp: boolean,
    canChangeBeacon = true
  ) {
    super(
      advertiserId,
      creativeTypeSetting.channelGroupId,
      creativeApprovalConfigs,
      forPmp,
      canChangeBeacon
    );
    this.validateDuration = this.validateDuration.bind(this);
  }

  override get screenDisplayType() {
    return this.channelGroupSetting.getScreenDisplayTypeByCreativeType(
      this.creativeTypeSetting.bannerTypeId
    );
  }

  override get useMediaDuration() {
    return true;
  }

  /**
   * 電子紙的圖片素材秒數由操作人員在廣告活動類型設定的區間內自訂（ECOPD-5659）。
   * 其他渠道維持固定選項，所以這裡只對電子紙給出區間。
   */
  override get durationRange() {
    if (!isEpaperChannelGroup(this.channelId)) {
      return undefined;
    }
    return {
      min: this.creativeTypeSetting.minDuration,
      max: this.creativeTypeSetting.maxDuration
    };
  }

  override get sizeBoxingMap(): {
    [key: string]: { width: [number, number]; height: [number, number] };
  } {
    if (this.screenDisplayType !== ScreenDisplayType.SINGLE) {
      return {};
    }
    const creativeSize = `${this.creativeTypeSetting.width}x${this.creativeTypeSetting.height}`;
    const widths =
      this.creativeTypeSetting.width > this.creativeTypeSetting.height
        ? [
            Math.floor(this.creativeTypeSetting.width * 0.7) + 1,
            this.creativeTypeSetting.width
          ]
        : [this.creativeTypeSetting.width, this.creativeTypeSetting.width];
    const heights =
      this.creativeTypeSetting.height > this.creativeTypeSetting.width
        ? [
            Math.floor(this.creativeTypeSetting.height * 0.7) + 1,
            this.creativeTypeSetting.height
          ]
        : [this.creativeTypeSetting.height, this.creativeTypeSetting.height];
    return {
      [creativeSize]: {
        width: widths as [number, number],
        height: heights as [number, number]
      }
    };
  }

  override getFormContent(): (
    props: OutdoorFormProps<DynamicOutdoorFormModel>
  ) => JSX.Element {
    return OutdoorForm<DynamicOutdoorFormModel>;
  }

  override getInitTypeProperties() {
    return {
      ...super.getInitTypeProperties(),
      orderName: '',
      orderStartDate: moment().startOf('day').format('YYYY-MM-DD'),
      orderEndDate: moment().startOf('day').add(1, 'day').format('YYYY-MM-DD'),
      duration:
        this.creativeTypeSetting.maxDuration ||
        this.creativeTypeSetting.minDuration
    };
  }

  override getDefaultDuration = (
    _newType: OutdoorType,
    _oldDuration?: number
  ) => {
    return (
      this.creativeTypeSetting.maxDuration ||
      this.creativeTypeSetting.minDuration
    );
  };

  override getVideoHints(width: number, height: number) {
    return [
      ...super.getVideoHints(width, height),
      i18n.t(
        this.creativeTypeSetting.maxDuration
          ? 'picFormModel.hints.videoDuration'
          : 'picFormModel.hints.videoDurationMin',
        {
          min: this.creativeTypeSetting.minDuration,
          max: this.creativeTypeSetting.maxDuration
        }
      )
    ];
  }

  override getAudioHints() {
    return [
      ...super.getAudioHints(),
      i18n.t('picFormModel.hints.audioDuration', {
        min: this.creativeTypeSetting.minDuration,
        max: this.creativeTypeSetting.maxDuration
      })
    ];
  }

  get screenProperty() {
    return (
      this.channelGroupSetting.config.screenProperty || {
        isScreenSpliced: false,
        screenSplicedHorizontalScreenCount: 1,
        screenSplicedVerticalScreenCount: 1,
        screenPixelHeight: 1920,
        screenPixelWidth: 1080
      }
    );
  }

  override getOutdoorTypeOptions() {
    const notSupportedOutdoorTypes = this.screenProperty.isScreenSpliced
      ? [OutdoorType.HTML5, OutdoorType.VIDEO_IMAGE]
      : [];
    return this.creativeTypeSetting.supportedOutdoorTypes
      .filter((type) => !notSupportedOutdoorTypes.includes(type))
      .map((type) => ({
        label: getOutdoorTypeLabel(type, this.channelId),
        value: type
      }));
  }

  override async getCreativeSizes(): Promise<CreativeSize[] | undefined> {
    return [
      {
        width: this.creativeTypeSetting.width,
        height: this.creativeTypeSetting.height
      }
    ];
  }

  override validateVideo(
    targetWidth: number,
    targetHeight: number,
    fileData: MediaFieldData
  ) {
    const errors = super.validateVideo(targetWidth, targetHeight, fileData);
    if (typeof errors === 'string') {
      return errors;
    }

    const error =
      fileData.duration !== undefined
        ? this.validateDuration(floor(fileData.duration))
        : undefined;
    if (error) {
      return i18n.t(
        this.creativeTypeSetting.maxDuration
          ? 'picFormModel.hints.videoDuration'
          : 'picFormModel.hints.videoDurationMin',
        {
          min: this.creativeTypeSetting.minDuration,
          max: this.creativeTypeSetting.maxDuration
        }
      );
    }

    return errors;
  }

  override validateAudio(fileData: MediaFieldData) {
    const errors = super.validateAudio(fileData);
    if (typeof errors === 'string') {
      return errors;
    }

    if (!this.hasFile(fileData)) {
      return;
    }

    const error = this.validateDuration(floor(fileData.duration));
    if (error) {
      return i18n.t('picFormModel.hints.audioDuration', {
        min: this.creativeTypeSetting.minDuration,
        max: this.creativeTypeSetting.maxDuration
      });
    }

    return errors;
  }

  validateDuration(value: number): string | undefined {
    const emptyError = validateEmpty(value);
    if (emptyError) {
      return emptyError;
    }
    const { minDuration, maxDuration } = this.creativeTypeSetting;
    const withUnit = (seconds: number) =>
      `${seconds} ${i18n.t('common.units.seconds')}`;
    const seconds = Number(value);
    if (seconds < minDuration) {
      return i18n.t('formValidate.labels.minimumError', {
        min: withUnit(minDuration)
      });
    }
    if (maxDuration && seconds > maxDuration) {
      return i18n.t('formValidate.labels.maximumError', {
        max: withUnit(maxDuration)
      });
    }
  }
}
